import { ConsoleLogger, fetchAuthSession } from "@aws-amplify/core";
import {
  ChimeSDKMessagingClient,
  ListChannelMembershipsForAppInstanceUserCommand,
  ListChannelMessagesCommand,
  SendChannelMessageCommand,
  UpdateChannelReadMarkerCommand,
} from "@aws-sdk/client-chime-sdk-messaging";
import { DefaultMessagingSession } from "amazon-chime-sdk-js";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../hooks";

class MessagingManager {

  /**
   * Initializes a messaging manager

   * @constructs MessagingManager
   * @param credentials {Object} The chime messaging credentials
   */
  constructor(credentials) {
    this.client = new ChimeSDKMessagingClient({
      region: "us-east-1",
      credentials,
    });

    this._logger = new ConsoleLogger(`Teleassistance-${crypto.randomUUID()}`);
    this.messagingSession = null;
  }

  /**
   * Joins a chime meeting

  * @param {Object} messagingSessionConfiguration The messaging session configuration
   */
  join(messagingSessionConfiguration) {
    this.messagingSession = new DefaultMessagingSession(
      messagingSessionConfiguration,
      this._logger,
    );
  }

  /**
   * Leaves a chime meeting
   */
  leave() {
    this.messagingSession?.stop();
    this.messagingSession = null;
  }

  /**
   * Lists channel messages
   * 
   * @param {Object} payload The request payload
   */
  async listChannelMessages(payload) {
    const listChannelMessagesCommand = new ListChannelMessagesCommand(payload);

    return this.client?.send(listChannelMessagesCommand);
  }

  /**
   * Gets the read marker timestamp
   * 
   * @param {Object} payload The request payload
   */
  async getReadMarkerTimestamp(payload) {
    const listChannelMembershipsForAppInstanceUserCommand =
      new ListChannelMembershipsForAppInstanceUserCommand(payload);

    return this.client?.send(listChannelMembershipsForAppInstanceUserCommand);
  }

  /**
   * Updates the read marker timestamp
   * 
   * @param {Object} payload The request payload
   */
  async updateReadMarkerTimestamp(payload) {
    const updateChannelReadMarkerCommand = new UpdateChannelReadMarkerCommand(
      payload,
    );

    return this.client?.send(updateChannelReadMarkerCommand);
  }

  /**
   * Sends a channel message
   * 
   * @param {Object} payload The request payload
   */
  async sendChannelMessage(payload) {
    const sendChannelMessageCommand = new SendChannelMessageCommand(payload);

    return this.client?.send(sendChannelMessageCommand);
  }

  /**
   * Starts the messaging session
   */
  start() {
    this.messagingSession?.start();
  }
}

/**
 * Hook that returns the messaging manager
 * 
 * @returns The messaging manager
 */
const useMessagingManager = () => {
  const { user } = useAuth()
  const [temporaryCredentials, setTemporaryCredentials] = useState({})

  useEffect(() => {
    const loadTemporaryCredentials = async () => {
      const user = await fetchAuthSession()

      setTemporaryCredentials(user?.credentials)
    }
    if (user) {
      loadTemporaryCredentials()
    }
  }, [user])

  const messagingManager = useMemo(
    () =>
      new MessagingManager(temporaryCredentials),
  [temporaryCredentials]);

  return messagingManager;
};

export default useMessagingManager;
